import React, { useEffect, useState } from 'react'
import Breadcrum from '../../components/breadcrum'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { blockUser, getAllUser, unblockUser } from '../../api/userApi'
import Swal from 'sweetalert2'
import {
  FaRegCheckCircle,
  FaSearch,
  FaFilter,
  FaUsers,
  FaUserCheck,
  FaUserTimes,
  FaShieldAlt,
} from 'react-icons/fa'
import { FaCircleXmark } from 'react-icons/fa6'
import { handleDate } from '../../handleDateAndPdf'
import Pagination from '../../components/Pagination'

const User = () => {
  useEffect(() => {
    document.title = 'User Management'
  }, [])

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [page, setPage] = useState(1)

  const queryClient = useQueryClient()

  const blockMutation = useMutation({
    mutationFn: (id) => blockUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['user'])
      toast.success('User has been blocked')
    },
    onError: () => {
      toast.error('Failed to block user')
    },
  })

  const unblockMutation = useMutation({
    mutationFn: (id) => unblockUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['user'])
      toast.success('User has been unblocked')
    },
    onError: () => {
      toast.error('Failed to unblock user')
    },
  })

  const { data, isLoading, isError } = useQuery({
    queryKey: ['user', page],
    queryFn: () => getAllUser(page),
    onError: (error) => {
      toast.error('Error while fetching data')
    },
  })

  const totalPages = Math.ceil((data?.totalCount || 0) / 10)

  // Filter and search logic
  const filteredUsers =
    data?.users.filter((user) => {
      const matchesSearch =
        user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.mobile?.includes(searchTerm)

      const matchesStatus =
        statusFilter === 'ALL' || user.status === statusFilter

      return matchesSearch && matchesStatus
    }) || []

  const stats = {
    total: data?.totalCount || 0,
    active: data?.activeUsersCount || 0,
    blocked: data?.blockedUsersCount || 0,
    verified: data?.verifiedUsersCount || 0,
  }

  if (isLoading) {
    return (
      <>
        <Breadcrum subheading="All user" heading="user management" />
        <div className="flex items-center justify-center min-h-[500px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 font-medium">
              Loading user data...
            </p>
          </div>
        </div>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <Breadcrum subheading="All user" heading="user management" />
        <div className="flex items-center justify-center min-h-[500px]">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Failed to Load Users
            </h3>
            <p className="text-gray-600">
              Unable to fetch user data. Please try again later.
            </p>
          </div>
        </div>
      </>
    )
  }

  const handleStatusToggle = async (e, user) => {
    const isChecked = e.target.checked

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: isChecked
        ? `You are about to block ${user.fullName}`
        : `You are about to unblock ${user.fullName}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, proceed',
      cancelButtonText: 'Cancel',
      confirmButtonColor: isChecked ? '#dc2626' : '#16a34a',
      cancelButtonColor: '#6b7280',
    })

    if (result.isConfirmed) {
      if (isChecked) {
        blockMutation.mutate(user.id)
      } else {
        unblockMutation.mutate(user.id)
      }
    } else {
      // Revert checkbox state manually if cancelled
      e.target.checked = !isChecked
    }
  }

  return (
    <>
      <Breadcrum subheading="All user" heading="user management" />

      {/* Header Section */}
      <div className="md:w-[95%] mx-auto mb-8">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">User Management</h1>
              <p className="text-blue-100 text-lg">
                Manage and monitor all registered users
              </p>
            </div>
            <div className="flex items-center gap-2">
              <FaShieldAlt className="w-8 h-8 text-blue-200" />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="md:w-[95%] mx-auto mb-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Total Users
                </p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {stats.total}
                </p>
              </div>
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <FaUsers className="w-7 h-7 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Active Users
                </p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  {stats.active}
                </p>
              </div>
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <FaUserCheck className="w-7 h-7 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Blocked Users
                </p>
                <p className="text-3xl font-bold text-red-600 mt-1">
                  {stats.blocked}
                </p>
              </div>
              <div className="w-14 h-14 bg-red-100 rounded-xl flex items-center justify-center">
                <FaUserTimes className="w-7 h-7 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                  Verified Emails
                </p>
                <p className="text-3xl font-bold text-purple-600 mt-1">
                  {stats.verified}
                </p>
              </div>
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                <FaRegCheckCircle className="w-7 h-7 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="md:w-[95%] mx-auto mb-6">
        <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by name, email, or mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#ff7010] focus:border-transparent outline-none transition-all"
              />
            </div>
            <div className="relative">
              <FaFilter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-12 pr-8 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white min-w-40"
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active</option>
                <option value="BLOCKED">Blocked</option>
              </select>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Showing {filteredUsers.length} of {stats.total} users
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="md:w-[95%] mx-auto">
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    #
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    User Info
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    Contact
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    Activity
                  </th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-700">
                    Status
                  </th>
                  <th className="py-4 px-4 text-center text-sm font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user, index) => (
                  <tr
                    key={user.id}
                    className="hover:bg-gradient-to-r hover:from-blue-25 hover:to-indigo-25 transition-all duration-200"
                  >
                    {/* Index */}
                    <td className="py-6 px-4">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                        {index + 1}
                      </div>
                    </td>

                    {/* User Info */}
                    <td className="py-6 px-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {user.fullName?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 capitalize">
                              {user.fullName || 'Unknown User'}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: #{user.id}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Contact */}
                    <td className="py-6 px-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-sm text-gray-700 max-w-48 truncate"
                            title={user.email}
                          >
                            {user.email}
                          </span>
                          {user.emailVerified ? (
                            <div className="relative group">
                              <FaRegCheckCircle className="w-4 h-4 text-green-600" />
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                Verified
                              </div>
                            </div>
                          ) : (
                            <div className="relative group">
                              <FaCircleXmark className="w-4 h-4 text-red-600" />
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                Not Verified
                              </div>
                            </div>
                          )}
                        </div>
                        {user.mobile && (
                          <div className="text-sm text-gray-600">
                            📱 {user.mobile}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Activity */}
                    <td className="py-6 px-4">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-700">
                          <span className="font-medium">Joined:</span>{' '}
                          {handleDate(user.createdAt)}
                        </div>
                        {user.lastLogin && (
                          <div className="text-sm text-gray-600">
                            <span className="font-medium">Last Login:</span>{' '}
                            {handleDate(user.lastLogin)}
                          </div>
                        )}
                        {user.blockedAt && (
                          <div className="text-sm text-red-600">
                            <span className="font-medium">Blocked:</span>{' '}
                            {handleDate(user.blockedAt)}
                          </div>
                        )}
                        {user.unblockedAt && (
                          <div className="text-sm text-green-600">
                            <span className="font-medium">Unblocked:</span>{' '}
                            {handleDate(user.unblockedAt)}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Status */}
                    <td className="py-6 px-4">
                      <div className="space-y-2">
                        <div
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            user.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : 'bg-red-100 text-red-800 border border-red-200'
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full mr-2 ${
                              user.status === 'ACTIVE'
                                ? 'bg-green-500'
                                : 'bg-red-500'
                            }`}
                          ></div>
                          {user.status}
                        </div>
                        <div
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                            user.emailVerified
                              ? 'bg-blue-100 text-blue-800 border border-blue-200'
                              : 'bg-gray-100 text-gray-800 border border-gray-200'
                          }`}
                        >
                          {user.emailVerified ? '✓ Verified' : '✗ Unverified'}
                        </div>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="py-6 px-4 text-center">
                      <div className="flex items-center justify-center">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={user.status === 'BLOCKED'}
                            onChange={(e) => handleStatusToggle(e, user)}
                            disabled={
                              blockMutation.isLoading ||
                              unblockMutation.isLoading
                            }
                          />
                          <div className="relative w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-red-500 hover:shadow-md transition-all"></div>
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            {user.status === 'BLOCKED' ? 'Unblock' : 'Block'}
                          </span>
                        </label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">👥</div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No Users Found
              </h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'ALL'
                  ? 'Try adjusting your search or filter criteria'
                  : 'No users have been registered yet'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-8 flex justify-center">
          <Pagination page={page} setPage={setPage} totalPages={totalPages} />
        </div>
      </div>
    </>
  )
}

export default User
